import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Card, CardContent, Divider, FormControlLabel, Grid, IconButton, MenuItem, Stack, Switch,
  Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import api from '../../services/api';
import { getApiErrorMessage } from '../../services/userService';
import {
  ACCOUNT_LABELS, ACCOUNT_TYPES, AccountType, PointsConfig, PointsEarnRule, SERVICE_LABELS,
  fetchRewardsConfig, useRewardsCurrency, useRewardsTenant,
} from './rewardsShared';

function num(value: string, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function int(value: string, fallback = 0) {
  return Math.floor(num(value, fallback));
}

export default function PointsSettingsPage() {
  const tenant = useRewardsTenant();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<PointsConfig | null>(null);
  const [accountTab, setAccountTab] = useState<AccountType>('user');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['rewards-config', tenant.scopeKey],
    queryFn: () => fetchRewardsConfig(tenant.headers),
    enabled: tenant.ready,
  });

  useEffect(() => {
    if (data) setDraft(structuredClone(data.points));
  }, [data]);

  const save = useMutation({
    mutationFn: (config: PointsConfig) => api.put('/admin/rewards/points-config', config, { headers: tenant.headers }),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Points settings saved.' });
      queryClient.invalidateQueries({ queryKey: ['rewards-config'] });
    },
    onError: (err) => setMessage({ type: 'error', text: getApiErrorMessage(err, 'Could not save points settings') }),
  });

  const { symbol, money } = useRewardsCurrency(data?.meta.currency);
  const services = data?.meta.services || Object.keys(SERVICE_LABELS);

  const account = draft?.accounts[accountTab];
  const setAccount = (next: PointsConfig['accounts'][AccountType]) =>
    setDraft((d) => d && ({ ...d, accounts: { ...d.accounts, [accountTab]: next } }));
  const setRule = (idx: number, patch: Partial<PointsEarnRule>) => {
    if (!account) return;
    setAccount({ ...account, rules: account.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  };
  const usedServices = new Set(account?.rules.map((r) => r.service) || []);
  const freeServices = ['default', ...services].filter((s) => !usedServices.has(s));

  const conv = draft?.conversion;
  const setConv = (patch: Partial<PointsConfig['conversion']>) =>
    setDraft((d) => d && ({ ...d, conversion: { ...d.conversion, ...patch } }));

  return (
    <PageShell>
      <PageHeader
        eyebrow="Referrals & Points"
        title="Points settings"
        subtitle="How customers, drivers and providers earn points on completed trips and jobs, and how points convert to wallet money."
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Points settings' }]}
        actions={(
          <Stack direction="row" spacing={1} alignItems="center">
            {tenant.picker}
            <Button variant="contained" startIcon={<Save />} disabled={!draft || save.isPending} onClick={() => draft && save.mutate(draft)}>
              Save
            </Button>
          </Stack>
        )}
      />
      {tenant.notReady}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(error, 'Could not load settings')}</Alert>}
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {data && !data.pointsConfigured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Customer earning currently follows the legacy loyalty rules. Saving here switches every app to this configuration.
        </Alert>
      )}
      {isLoading && <Typography color="text.secondary">Loading…</Typography>}

      {draft && account && conv && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={<Switch checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />}
                      label="Points program enabled"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Points expire after (days)"
                      helperText="0 = points never expire"
                      value={draft.expiryDays}
                      onChange={(e) => setDraft({ ...draft, expiryDays: int(e.target.value) })}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700}>Earning rules</Typography>
                <Tabs value={accountTab} onChange={(_, v) => setAccountTab(v)} sx={{ mb: 2 }}>
                  {ACCOUNT_TYPES.map((t) => <Tab key={t} value={t} label={ACCOUNT_LABELS[t]} />)}
                </Tabs>
                <FormControlLabel
                  control={<Switch checked={account.enabled} onChange={(e) => setAccount({ ...account, enabled: e.target.checked })} />}
                  label={`${ACCOUNT_LABELS[accountTab]}s earn points`}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Points = floor(amount ÷ per amount × points) + fixed points. A service rule replaces the default rule for that service;
                  switch it off to give no points for that service.
                </Typography>
                {account.rules.map((rule, idx) => (
                  <Box key={`${rule.service}-${idx}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <TextField
                        select
                        size="small"
                        label="Service"
                        sx={{ minWidth: 200 }}
                        value={rule.service}
                        onChange={(e) => setRule(idx, { service: e.target.value })}
                      >
                        {['default', ...services].filter((s) => s === rule.service || !usedServices.has(s)).map((s) => (
                          <MenuItem key={s} value={s}>{s === 'default' ? 'Default (all services)' : SERVICE_LABELS[s] || s}</MenuItem>
                        ))}
                      </TextField>
                      <FormControlLabel
                        control={<Switch size="small" checked={rule.enabled} onChange={(e) => setRule(idx, { enabled: e.target.checked })} />}
                        label="Enabled"
                      />
                      <Box flex={1} />
                      <IconButton
                        aria-label="Remove rule"
                        onClick={() => setAccount({ ...account, rules: account.rules.filter((_, i) => i !== idx) })}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" type="number" label="Points" value={rule.pointsPerUnit}
                          onChange={(e) => setRule(idx, { pointsPerUnit: num(e.target.value) })} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" type="number" label={`Per amount (${symbol || 'amount'})`} value={rule.perAmount}
                          onChange={(e) => setRule(idx, { perAmount: Math.max(0.01, num(e.target.value, 1)) })} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" type="number" label="Fixed points" value={rule.fixedPoints}
                          onChange={(e) => setRule(idx, { fixedPoints: int(e.target.value) })} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" type="number" label={`Min amount (${symbol || 'amount'})`} value={rule.minAmount}
                          onChange={(e) => setRule(idx, { minAmount: num(e.target.value) })} />
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Example: a {money(500)} trip earns{' '}
                      {rule.enabled && 500 >= rule.minAmount
                        ? Math.floor((500 / (rule.perAmount || 1)) * rule.pointsPerUnit) + rule.fixedPoints
                        : 0}{' '}
                      points (before tier multiplier).
                    </Typography>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<Add />}
                  disabled={!freeServices.length}
                  onClick={() => setAccount({
                    ...account,
                    rules: [...account.rules, { service: freeServices[0], enabled: true, pointsPerUnit: 1, perAmount: 100, fixedPoints: 0, minAmount: 0 }],
                  })}
                >
                  Add rule
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Convert to wallet</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <FormControlLabel control={<Switch checked={conv.enabled} onChange={(e) => setConv({ enabled: e.target.checked })} />} label="Conversion enabled" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label={symbol ? `Points per ${symbol}1` : 'Points per 1 unit'} value={conv.pointsPerUnit}
                      helperText={`${conv.pointsPerUnit || 0} points = ${money(1)}`}
                      onChange={(e) => setConv({ pointsPerUnit: Math.max(0.0001, num(e.target.value, 1)) })} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="Minimum points" value={conv.minPoints}
                      onChange={(e) => setConv({ minPoints: int(e.target.value) })} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="Max points / day" helperText="0 = unlimited" value={conv.maxPointsPerDay}
                      onChange={(e) => setConv({ maxPointsPerDay: int(e.target.value) })} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="Max points / month" helperText="0 = unlimited" value={conv.maxPointsPerMonth}
                      onChange={(e) => setConv({ maxPointsPerMonth: int(e.target.value) })} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel control={<Switch checked={conv.allowManual} onChange={(e) => setConv({ allowManual: e.target.checked })} />} label="Users can convert from the app" />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel control={<Switch checked={conv.autoConvert} onChange={(e) => setConv({ autoConvert: e.target.checked })} />} label="Auto-convert" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label="Auto-convert at (points)" disabled={!conv.autoConvert} value={conv.autoThreshold}
                      onChange={(e) => setConv({ autoThreshold: int(e.target.value) })} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>Tiers</Typography>
                  <FormControlLabel
                    control={<Switch checked={draft.tiersEnabled} onChange={(e) => setDraft({ ...draft, tiersEnabled: e.target.checked })} />}
                    label="Enabled"
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Tier is based on lifetime points; the multiplier boosts points earned on each completion.
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                {draft.tiers.map((tier, idx) => (
                  <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }}>
                    <TextField size="small" label="Name" value={tier.name} disabled={!draft.tiersEnabled}
                      onChange={(e) => setDraft({ ...draft, tiers: draft.tiers.map((t, i) => (i === idx ? { ...t, name: e.target.value } : t)) })} />
                    <TextField size="small" type="number" label="From lifetime pts" value={tier.minPoints} disabled={!draft.tiersEnabled}
                      onChange={(e) => setDraft({ ...draft, tiers: draft.tiers.map((t, i) => (i === idx ? { ...t, minPoints: int(e.target.value) } : t)) })} />
                    <TextField size="small" type="number" label="Multiplier" value={tier.multiplier} disabled={!draft.tiersEnabled}
                      inputProps={{ step: '0.05' }}
                      onChange={(e) => setDraft({ ...draft, tiers: draft.tiers.map((t, i) => (i === idx ? { ...t, multiplier: num(e.target.value, 1) } : t)) })} />
                    <IconButton aria-label="Remove tier" disabled={!draft.tiersEnabled || draft.tiers.length <= 1}
                      onClick={() => setDraft({ ...draft, tiers: draft.tiers.filter((_, i) => i !== idx) })}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button size="small" startIcon={<Add />} disabled={!draft.tiersEnabled}
                  onClick={() => setDraft({ ...draft, tiers: [...draft.tiers, { name: 'new tier', minPoints: 0, multiplier: 1 }] })}>
                  Add tier
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </PageShell>
  );
}
